import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useState } from "react";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { getDashboardBalanceCurrencyKey } from "Utils/Storage/LocalStorage/LocalStorageUtils";

type Selection = {
  storageKey: string;
  currency: string | null;
};

/**
 * The currency the dashboard balance shows (the mobile hero and the desktop card share one
 * choice, owned by BalanceContainer). Resolution order: the persona's stored choice
 * if it is still one of their currencies, else the market currency if they hold it, else the
 * first currency. The choice lives in local storage per persona; a stale value (account closed,
 * currency gone) is simply ignored, never cleared.
 */
export const useDashboardBalanceCurrency = (
  currencies: ReadonlyArray<string>,
  marketCurrency?: string,
) => {
  const user = useLoggedInUser();
  const storageKey = getDashboardBalanceCurrencyKey(
    user?.activeContextPublicID ?? "anonymous",
  );

  // Read once per storage key. A persona switch changes the key: re-read once and store the
  // result during render (React re-runs the render right away), so no later render touches
  // local storage. Nothing is written until the user picks a currency.
  const [selection, setSelection] = useState<Selection>(() => ({
    storageKey,
    currency: getLocalStorageItem(storageKey),
  }));
  let storedCurrency = selection.currency;
  if (selection.storageKey !== storageKey) {
    storedCurrency = getLocalStorageItem(storageKey);
    setSelection({ storageKey, currency: storedCurrency });
  }

  const defaultCurrency =
    marketCurrency && currencies.includes(marketCurrency)
      ? marketCurrency
      : currencies[0];
  const selectedCurrency =
    storedCurrency && currencies.includes(storedCurrency)
      ? storedCurrency
      : defaultCurrency;

  const selectCurrency = (currency: string) => {
    setLocalStorageItem(storageKey, currency);
    setSelection({ storageKey, currency });
  };

  return { selectedCurrency, selectCurrency };
};
